import React from "react";
import styled from "styled-components";

const NoisePadPrivacyPolicy = () => {
  return (
    <Page>
      <Content>
        <Title>Noise Pad – Privacy Policy</Title>

        <Meta>
          <p><strong>Effective date:</strong> October 12, 2025</p>
          <p><strong>App:</strong> Noise Pad (iOS)</p>
          <p><strong>Contact:</strong> <a href="mailto:onorergun@gmail.com">onorergun@gmail.com</a></p>
        </Meta>

        <Section>
          <p>
            This Privacy Policy explains how Noise Pad (“we,” “our,” “us”) collects, uses, and protects your
            information when you use our mobile application and related services (the “Services”).
          </p>
          <p>
            Noise Pad measures environmental sound levels and displays them on a map. The app does not record
            or store raw audio. With your permission, it accesses your microphone to measure sound levels and
            your location to show where the measurement occurred.
          </p>
        </Section>

        <Divider />

        <Section>
          <h2>1. Summary</h2>
          <ul>
            <li>We collect minimal data: email, password, username (nickname), and noise measurements (decibel level, timestamp, and location).</li>
            <li>We use AWS Cognito for authentication and AWS DynamoDB for cloud storage.</li>
            <li>You can delete your account anytime inside the app, which removes your personal data.</li>
            <li>Noise data remains only in anonymized form for statistical and community map purposes.</li>
            <li>We do not sell, track, or advertise.</li>
            <li>Noise Pad is designed for users aged 13 and older.</li>
          </ul>
        </Section>

        <Divider />

        <Section>
          <h2>2. Information We Collect</h2>

          <h3>A. Account Information</h3>
          <ul>
            <li><strong>Email address</strong> – used for login and password reset</li>
            <li><strong>Username (nickname)</strong> – a display name chosen by you, not required to match your real name</li>
            <li><strong>Password</strong> – securely stored by AWS Cognito, never in plaintext</li>
          </ul>

          <h3>B. Measurement Data</h3>
          <p>When you record a sound level, the app collects:</p>
          <ul>
            <li>Decibel level</li>
            <li>Timestamp (UTC)</li>
            <li>Location (latitude and longitude)</li>
            <li>Username (nickname)</li>
          </ul>
          <p>This data is stored to display community noise levels. No raw audio is recorded.</p>

          <h3>C. Device and Technical Data</h3>
          <p>We collect basic technical information (device model, OS version, crash logs) to maintain performance and reliability.</p>
        </Section>

        <Divider />

        <Section>
          <h2>3. How We Use the Data</h2>
          <ul>
            <li>To create and secure user accounts</li>
            <li>To display and sync noise measurements</li>
            <li>To maintain and improve app performance</li>
            <li>To ensure fair use and prevent abuse</li>
            <li>To comply with legal requirements</li>
          </ul>
          <p>We do not use data for marketing, profiling, or behavioral analytics.</p>
        </Section>

        <Divider />

        <Section>
          <h2>4. Permissions</h2>
          <ul>
            <li><strong>Microphone:</strong> Used only to calculate decibel readings. The app never records or transmits audio.</li>
            <li><strong>Location:</strong> Used only to associate your measurement with a map location.</li>
          </ul>
          <p>You can revoke these permissions anytime in your iOS Settings.</p>
        </Section>

        <Divider />

        <Section>
          <h2>5. Data Categories for App Store Disclosure</h2>
          <ul>
            <li><strong>Contact Info:</strong> Email</li>
            <li><strong>Identifiers:</strong> Username (nickname), User ID</li>
            <li><strong>Location:</strong> Precise location</li>
            <li><strong>User Content:</strong> Noise measurement data (decibel level, time, coordinates)</li>
            <li><strong>Diagnostics:</strong> Crash data</li>
          </ul>
          <p>We do not use any data for tracking or advertising.</p>
        </Section>

        <Divider />

        <Section>
          <h2>6. Data Sharing</h2>
          <p>Noise Pad uses Amazon Web Services (AWS) as its infrastructure provider:</p>
          <ul>
            <li>AWS Cognito manages sign-up, sign-in, and authentication</li>
            <li>AWS DynamoDB stores anonymized measurement data</li>
          </ul>
          <p>No third-party analytics, ads, or marketing integrations are used. Data is disclosed only if required by law or to prevent security misuse.</p>
        </Section>

        <Divider />

        <Section>
          <h2>7. International Data Storage</h2>
          <p>Data is stored in AWS EU-North-1 (Stockholm), protected under strict European data standards (GDPR-compliant).</p>
        </Section>

        <Divider />

        <Section>
          <h2>8. Data Retention and Deletion</h2>
          <ul>
            <li>Account data (email, username, password hash) is deleted immediately when you delete your account through the app.</li>
            <li>Noise measurements (decibel level, timestamp, location) remain stored but are anonymized — they contain no email, no real name, and only your nickname, which cannot identify you personally.</li>
            <li>These anonymized measurements are retained indefinitely for public-interest features such as city noise mapping, research, and analytics.</li>
            <li>All backups are rotated and automatically purged within a reasonable time frame.</li>
          </ul>
          <p>You do not need to contact us to delete your account — this can be done directly within the app. For additional privacy inquiries, you can email <a href="mailto:onorergun@gmail.com">onorergun@gmail.com</a>.</p>
        </Section>

        <Divider />

        <Section>
          <h2>9. Your Rights</h2>
          <p>You can:</p>
          <ul>
            <li>Delete your account directly in the app</li>
            <li>Access, review, or correct your profile information</li>
            <li>Withdraw permissions for location or microphone access</li>
            <li>Request confirmation of anonymization or deletion via email</li>
          </ul>
          <p>Contact <a href="mailto:onorergun@gmail.com">onorergun@gmail.com</a> for assistance if needed.</p>
        </Section>

        <Divider />

        <Section>
          <h2>10. Security</h2>
          <p>Noise Pad uses industry-standard encryption and AWS-managed authentication. All communications occur over HTTPS (TLS 1.2+). We continuously monitor systems for unauthorized access or misuse.</p>
          <p>While no digital system is perfectly secure, we take all reasonable steps to protect your information.</p>
        </Section>

        <Divider />

        <Section>
          <h2>11. Children’s Privacy</h2>
          <p>Noise Pad is intended for users aged 13 and older. We do not knowingly collect or store data from anyone under 13 (or under 16 in some jurisdictions). If we learn that a child has created an account, we will delete the account and anonymize all related data.</p>
          <p>Parents or guardians may contact <a href="mailto:onorergun@gmail.com">onorergun@gmail.com</a> for assistance.</p>
        </Section>

        <Divider />

        <Section>
          <h2>12. Third-Party Links</h2>
          <p>Noise Pad does not include advertising or social plugins. If the app links to documentation or external resources, this Privacy Policy does not apply to those third-party sites.</p>
        </Section>

        <Divider />

        <Section>
          <h2>13. Updates to This Policy</h2>
          <p>This Privacy Policy may be updated from time to time to reflect app improvements or legal requirements. Any updates will include a new “Effective Date” at the top of this page.</p>
        </Section>

        <Divider />

        <Section>
          <h2>14. Contact</h2>
          <p>For all privacy questions, deletion confirmations, or general inquiries, please contact:</p>
          <p><strong>Email:</strong> <a href="mailto:onorergun@gmail.com">onorergun@gmail.com</a></p>
        </Section>

        <Divider />

        <Section>
          <h2>Info.plist Usage Descriptions</h2>
          <ul>
            <li><strong>NSLocationWhenInUseUsageDescription:</strong> “Noise Pad uses your location to tag sound measurements on the map.”</li>
            <li><strong>NSMicrophoneUsageDescription:</strong> “Noise Pad uses the microphone to calculate a decibel level. It does not record audio.”</li>
          </ul>
        </Section>

        <FooterNote>Last updated: October 12, 2025</FooterNote>
      </Content>
    </Page>
  );
};

export default NoisePadPrivacyPolicy;

// Styled-components
const Page = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 20px;
  color: #fff;
`;

const Content = styled.div`
  width: 100%;
  max-width: 900px;
  line-height: 1.7;
`;

const Title = styled.h1`
  font-family: "Heiti SC", sans-serif;
  font-size: clamp(22px, 3.2vw, 36px);
  margin: 0 0 24px 0;
`;

const Meta = styled.div`
  font-family: "Heiti SC", sans-serif;
  font-size: clamp(13px, 1.1vw, 16px);
  opacity: 0.9;
  margin-bottom: 16px;

  a {
    color: #ffcc00;
    text-decoration: underline;

    &:hover {
      color: #ffd633;
    }
  }
`;

const Section = styled.div`
  font-family: "Heiti SC", sans-serif;
  font-size: clamp(14px, 1.2vw, 18px);

  h2 {
    margin-top: 24px;
    font-size: clamp(18px, 2vw, 24px);
  }

  h3 {
    margin-top: 18px;
    font-size: clamp(16px, 1.6vw, 20px);
  }

  p, li {
    margin: 10px 0;
  }

  ul {
    padding-left: 20px;
  }

  a {
    color: #ffcc00;
    text-decoration: underline;

    &:hover {
      color: #ffd633;
    }
  }
`;
const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255,255,255,0.15);
  margin: 24px 0;
`;

const FooterNote = styled.p`
  font-family: "Heiti SC", sans-serif;
  font-size: clamp(12px, 1vw, 14px);
  opacity: 0.75;
  margin-top: 24px;
`;